import React, { useEffect, useRef, useState } from 'react'
import { Slider } from 'sutra/components/Slider/Slider'

import {
  PaneContainer,
  PaneDivider,
  PaneEyebrow,
  RatingNullCheckbox,
  RatingNullLabel,
  RatingNullRow,
  RatingSliderWrap,
} from 'features/charts-v2/components/filter-popover-pane.styles'
import { FilterPopoverPaneFooter } from 'features/charts-v2/components/FilterPopoverPaneFooter/FilterPopoverPaneFooter'
import { RatingTypeSelector } from 'features/charts-v2/components/RatingTypeSelector/RatingTypeSelector'
import { FilterOptionRange } from 'features/filters/types'

// "Ratings Only" excludes review text entirely, so a null-score-but-has-review
// row can never match it — selecting this type always clears the "no star
// rating" inclusion (see handleValueTypeChange).
const RATINGS_ONLY_VALUE_TYPE = 2

interface RatingCommitted {
  valueType: number
  min: number
  max: number
  includeNull: boolean
  hasCommittedFilter: boolean
}

interface FilterPopoverRatingPaneProps {
  rangeFilter: FilterOptionRange
  showNullOption: boolean
  nullOptionLabel: string
  // Restricts which Type radios may be newly selected — e.g. a dashboard
  // global Rating filter allows only Reviews Only. Undefined = unrestricted.
  // Forwarded to `RatingTypeSelector` — see its own doc comment.
  allowedValueTypes?: readonly number[]
  // Currently-committed draft inputs, pre-resolved by the caller from
  // whichever selection shape is on the panel (discrete star values from the
  // card popover, or an existing window range) into this pane's range shape.
  committed: RatingCommitted
  onApply: (draft: {
    valueType: number
    min: number
    max: number
    includeNull: boolean
  }) => void
  onClear: () => void
}

// In-popover editor for the Rating filter (score/post_score). Like the
// Records/Sentences pane, nothing writes to the panel until Apply — radio
// clicks, slider drags, and the null-rows checkbox all edit draft state only.
export const FilterPopoverRatingPane: React.FC<
  FilterPopoverRatingPaneProps
> = ({
  rangeFilter,
  showNullOption,
  nullOptionLabel,
  allowedValueTypes,
  committed,
  onApply,
  onClear,
}) => {
  const [draftValueType, setDraftValueType] = useState(committed.valueType)
  const [draftMin, setDraftMin] = useState(committed.min)
  const [draftMax, setDraftMax] = useState(committed.max)
  const [draftIncludeNull, setDraftIncludeNull] = useState(
    committed.includeNull,
  )

  // `committed` also moves when the panel-narrowed rating bounds LAND, not just
  // when the filter changes externally: the caller seeds min/max from
  // `cur_min ?? min` / `cur_max ?? max`, `stripFilterCounts` strips `cur_*`, and
  // opening this pane is what triggers the per-field counts fetch. So a fetch
  // resolving mid-drag used to overwrite the draft and disarm Apply. Track
  // whether the user has edited: an untouched draft still adopts fresh bounds
  // (they are the more accurate range), an edited one is never clobbered.
  const hasEditedDraft = useRef(false)

  // Re-sync the draft when the committed filter changes externally (Apply
  // round-tripping through the panel, or re-opening the pane on an already
  // -applied filter).
  useEffect(() => {
    if (hasEditedDraft.current) return
    setDraftValueType(committed.valueType)
    setDraftMin(committed.min)
    setDraftMax(committed.max)
    setDraftIncludeNull(committed.includeNull)
  }, [committed.valueType, committed.min, committed.max, committed.includeNull])

  // The null-rows row is shown while its count is unknown, so it can vanish when
  // the count lands as 0 — but `draftIncludeNull` lives here, not in the removed
  // subtree, so a box checked during that window would survive and Apply would
  // commit NULL_VALUE with no visible control saying so. Drop the inclusion with
  // the control.
  useEffect(() => {
    if (!showNullOption && draftIncludeNull) setDraftIncludeNull(false)
  }, [showNullOption, draftIncludeNull])

  // Selecting a type other than "Ratings Only" defaults null-score rows back
  // in (mirrors the historical always-commit behavior); "Ratings Only" has no
  // concept of a null star rating, so it always clears the inclusion.
  const handleValueTypeChange = (newValueType: number): void => {
    hasEditedDraft.current = true
    setDraftValueType(newValueType)
    setDraftIncludeNull(newValueType !== RATINGS_ONLY_VALUE_TYPE)
  }

  const handleIncludeNullToggle = (): void => {
    hasEditedDraft.current = true
    setDraftIncludeNull(!draftIncludeNull)
  }

  const sliderMin = rangeFilter.min ?? 1
  const sliderMax = rangeFilter.max ?? 5
  const sliderStep = rangeFilter.step ?? 1
  const marks: Array<{ value: number; label: string }> = []
  for (let i = sliderMin; i <= sliderMax; i += sliderStep) {
    marks.push({ value: i, label: String(i) })
  }

  const isDirty =
    draftValueType !== committed.valueType ||
    draftMin !== committed.min ||
    draftMax !== committed.max ||
    draftIncludeNull !== committed.includeNull

  return (
    <PaneContainer>
      <PaneEyebrow>Type</PaneEyebrow>
      <RatingTypeSelector
        value={draftValueType}
        onChange={handleValueTypeChange}
        allowedValues={allowedValueTypes}
      />

      <PaneDivider />
      <PaneEyebrow>Values</PaneEyebrow>
      <RatingSliderWrap>
        <Slider
          min={sliderMin}
          max={sliderMax}
          step={sliderStep}
          value={[draftMin, draftMax]}
          thumbLabels={['Minimum rating', 'Maximum rating']}
          onChange={(values) => {
            const [nextMin, nextMax] = values
            hasEditedDraft.current = true
            setDraftMin(nextMin ?? sliderMin)
            setDraftMax(nextMax ?? sliderMax)
          }}
          marks={marks}
        />
      </RatingSliderWrap>

      {showNullOption && (
        <RatingNullRow onClick={handleIncludeNullToggle}>
          <RatingNullCheckbox
            checked={draftIncludeNull}
            onChange={handleIncludeNullToggle}
          />
          <RatingNullLabel>
            {/* `Null` is stripped while this field's counts are in flight —
                show an em dash rather than a confident "(0)", which reads as
                "there are none" for a row we are showing precisely because we
                do not know yet. */}
            {nullOptionLabel} ({rangeFilter.Null ?? '—'})
          </RatingNullLabel>
        </RatingNullRow>
      )}

      <FilterPopoverPaneFooter
        applyDisabled={!isDirty}
        clearDisabled={!committed.hasCommittedFilter}
        onApply={() => {
          hasEditedDraft.current = false
          onApply({
            valueType: draftValueType,
            min: draftMin,
            max: draftMax,
            includeNull: draftIncludeNull,
          })
        }}
        onClear={() => {
          hasEditedDraft.current = false
          onClear()
        }}
      />
    </PaneContainer>
  )
}
